import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { DISCOUNTABLE_LINES, LINE_LABELS, fxFactor, round2, type MonitoringRates, type PricingResult } from '@atek/estimate-pricing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormField } from '@/shared/components/form';
import { priceToDiscount } from '../../lib/editorState';
import { uiLang } from '../../lib/format';
import { NativeSelect } from '../NativeSelect';
import { EditorSection } from './EditorSection';
import { NumberField } from './NumberField';
import type { SectionProps } from './types';

/** A typed unit price, committed on blur or Enter so a half-typed number is not reformatted under the cursor. */
function PriceInput({ value, onCommit, label, disabled }: { value: number; onCommit: (price: number) => void; label: string; disabled: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const commit = () => {
    if (text !== null && text.trim() !== '' && Number.isFinite(Number(text))) onCommit(Number(text));
    setText(null);
  };
  return (
    <Input
      type="number" inputMode="decimal" min={0} step={0.01} aria-label={label} disabled={disabled} className="w-28 text-right"
      value={text ?? String(value)}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
    />
  );
}

export function EquipmentSection({ draft, dispatch, readOnly, errors, rates, live }: SectionProps & { rates: MonitoringRates; live: PricingResult | null }) {
  const { t, i18n } = useTranslation('crm');
  const lang = uiLang(i18n.language);
  const { inputs } = draft;
  const [toAdd, setToAdd] = useState('');
  const types = new Map(rates.equipment.map((type) => [type.key, type]));
  // List prices in the estimate's currency, as the pricing engine converts them.
  const fx = fxFactor(inputs.currency, inputs.currency === 'CAD' ? 1 : inputs.fxRate, rates.fxMarkupPct);
  const available = rates.equipment.filter((type) => !inputs.equipment.some((row) => row.typeKey === type.key));
  const connectivity = inputs.connectivity;
  // The discount the engine applies to a line: the typed one, else automatic (the price book's volume tier, when it
  // has one). Read from the live result so the screen never re-implements the tiers.
  const appliedPct = (lineKey: string) => live?.fullScope.lines.find((line) => line.key === lineKey)?.discountPct ?? 0;
  const automatic = (typed: number | null | undefined, lineKey: string) => {
    if (typed != null) return undefined;
    const pct = appliedPct(lineKey);
    return pct > 0 ? String(pct) : undefined;
  };

  return (
    <EditorSection title={t('estimates.equipment.title')}>
      {errors['inputs.equipment'] && <p role="alert" className="text-xs text-destructive">{errors['inputs.equipment']}</p>}
      {inputs.equipment.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('estimates.equipment.type')}</TableHead>
              <TableHead className="w-24">{t('estimates.equipment.qty')}</TableHead>
              <TableHead className="w-24">{t('estimates.equipment.calibrated')}</TableHead>
              <TableHead className="w-32 text-right">{t('estimates.equipment.unitPrice', { currency: inputs.currency })}</TableHead>
              <TableHead className="w-28 text-right">{t('estimates.equipment.discount')}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {inputs.equipment.map((row, index) => {
              const type = types.get(row.typeKey);
              const label = type ? type.label[lang] : row.typeKey;
              const list = type ? round2(type.unitPrice * fx) : 0;
              const pct = row.discountPct ?? appliedPct(`equipment:${row.typeKey}`);
              const path = `inputs.equipment[${index}]`;
              const rowError = errors[`${path}.qty`] ?? errors[`${path}.typeKey`] ?? errors[`${path}.calibrated`] ?? errors[`${path}.discountPct`];
              return (
                <TableRow key={row.typeKey}>
                  <TableCell>
                    <div>{label}</div>
                    {rowError && <p role="alert" className="text-xs text-destructive">{rowError}</p>}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number" min={0} step={1} className="w-20" disabled={readOnly}
                      aria-label={t('estimates.equipment.qtyFor', { type: label })} aria-invalid={!!errors[`${path}.qty`]}
                      value={row.qty}
                      onChange={(e) => dispatch({ type: 'setEquipment', index, patch: { qty: e.target.value === '' ? 0 : Number(e.target.value) } })}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="checkbox" className="h-4 w-4" aria-label={t('estimates.equipment.calibratedFor', { type: label })}
                      checked={row.calibrated} disabled={readOnly || !type?.calibratable}
                      onChange={(e) => dispatch({ type: 'setEquipment', index, patch: { calibrated: e.target.checked } })}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <PriceInput
                      label={t('estimates.equipment.unitPriceFor', { type: label })}
                      disabled={readOnly}
                      value={round2(list * (1 - pct / 100))}
                      onCommit={(price) => dispatch({ type: 'setEquipment', index, patch: { discountPct: priceToDiscount(price, list) } })}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number" min={0} max={100} step={0.01} className="w-24 text-right" disabled={readOnly}
                      aria-label={t('estimates.equipment.discountFor', { type: label })}
                      value={row.discountPct ?? ''}
                      placeholder={automatic(row.discountPct, `equipment:${row.typeKey}`)}
                      onChange={(e) => dispatch({ type: 'setEquipment', index, patch: { discountPct: e.target.value === '' ? null : Number(e.target.value) } })}
                    />
                  </TableCell>
                  <TableCell>
                    {!readOnly && (
                      <Button
                        type="button" variant="ghost" size="icon" aria-label={t('estimates.equipment.removeFor', { type: label })}
                        onClick={() => dispatch({ type: 'removeEquipment', index })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {!readOnly && available.length > 0 && (
        <div className="flex items-end gap-2">
          <FormField label={t('estimates.equipment.add')} htmlFor="est-add-equipment" className="w-72">
            <NativeSelect id="est-add-equipment" value={toAdd} onChange={(e) => setToAdd(e.target.value)}>
              <option value="">{t('estimates.equipment.choose')}</option>
              {available.map((type) => <option key={type.key} value={type.key}>{type.label[lang]}</option>)}
            </NativeSelect>
          </FormField>
          <Button
            type="button" variant="outline" disabled={!toAdd}
            onClick={() => { dispatch({ type: 'addEquipment', typeKey: toAdd }); setToAdd(''); }}
          >
            <Plus className="h-4 w-4 mr-1" /> {t('estimates.equipment.addButton')}
          </Button>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <NumberField
          id="est-third-party" label={t('estimates.equipment.thirdParty')} hint={t('estimates.equipment.thirdPartyHint')}
          value={inputs.thirdPartySensors} disabled={readOnly} error={errors['inputs.thirdPartySensors']}
          onChange={(v) => dispatch({ type: 'setInputs', patch: { thirdPartySensors: v ?? 0 } })}
        />
        <FormField label={t('estimates.equipment.connectivity')} htmlFor="est-connectivity">
          <NativeSelect
            id="est-connectivity" value={connectivity.mode} disabled={readOnly}
            onChange={(e) => dispatch({ type: 'setConnectivity', mode: e.target.value as 'wifi' | 'gateway' })}
          >
            <option value="gateway">{t('estimates.equipment.connectivityGateway')}</option>
            <option value="wifi">{t('estimates.equipment.connectivityWifi')}</option>
          </NativeSelect>
        </FormField>
        {connectivity.mode === 'gateway' && (
          <div className="grid grid-cols-2 gap-2">
            <NumberField
              id="est-gateways" label={t('estimates.equipment.gateways')} value={connectivity.gateways} max={1000} disabled={readOnly}
              error={errors['inputs.connectivity.gateways']}
              onChange={(v) => dispatch({ type: 'setInputs', patch: { connectivity: { ...connectivity, gateways: v ?? 0 } } })}
            />
            <NumberField
              id="est-data-plans" label={t('estimates.equipment.dataPlans')} value={connectivity.dataPlans} max={1000} disabled={readOnly}
              error={errors['inputs.connectivity.dataPlans']}
              onChange={(v) => dispatch({ type: 'setInputs', patch: { connectivity: { ...connectivity, dataPlans: v ?? 0 } } })}
            />
          </div>
        )}
      </div>
      <details className="rounded-md border p-3">
        <summary className="cursor-pointer text-sm font-medium">{t('estimates.equipment.lineDiscounts')}</summary>
        <p className="mt-2 text-xs text-muted-foreground">{t('estimates.equipment.lineDiscountsHint')}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {DISCOUNTABLE_LINES.map((line) => (
            <NumberField
              key={line} id={`est-line-discount-${line}`} label={LINE_LABELS[line][lang]} allowEmpty max={100} step={0.01}
              value={inputs.lineDiscounts[line] ?? null} disabled={readOnly} error={errors[`inputs.lineDiscounts.${line}`]}
              placeholder={automatic(inputs.lineDiscounts[line], line)}
              onChange={(v) => dispatch({ type: 'setInputs', patch: { lineDiscounts: { ...inputs.lineDiscounts, [line]: v } } })}
            />
          ))}
        </div>
      </details>
    </EditorSection>
  );
}
